"use client";
import { trackWorkflow } from "./product-analytics";

// In-session stage-override store for the Creator Scripts demo.
//
// The end-to-end story — creator submits → AM reviews → brand approves →
// footage unlocks and repeats the same ladder — needs stage transitions to
// visibly stick as the user navigates between pages in one session. This module
// holds a client-side override map with a tiny subscribe API compatible with
// React's useSyncExternalStore, backed by persisted rows.
//
// The two ladders and their legal moves live in lib/stage-model.ts. Nothing
// here decides whether a transition is allowed; it asks, and drops a null.
//
// WHAT IT NOW ASKS WITH is the script's brand's SEAT — who gives that brand's
// final approval (lib/brand-approval.ts). Some brands do not review, and on
// their ladders the AM's approve lands on `approved` directly. Resolved per
// script from the draft's `brandSlug`, so the question stops here and no caller
// carries it. See seatFor.

import { uuidv7 } from "uuidv7";
import type { ScriptEntry } from "./ironside-script-data";
import type {
  ScriptStage, FootageStage, ScriptAction, FootageAction, Role, ScriptMove, FootageMove,
  FinalApprover,
} from "./stage-model";
import {
  nextScriptStage, nextFootageStage, footageUnlocked, canUnapproveScript,
  scriptMoveIsUndoable, footageMoveIsUndoable, undoneScriptStage, undoneFootageStage,
} from "./stage-model";
import type { ScriptFields, GradeResult } from "./ironside-script-grader";
import { SUBMIT_THRESHOLD } from "./ironside-script-grader";
import { isBlankFields, fieldsFromEntry, type DraftFields } from "./ironside-script-fields";
import type { GradeRecord } from "./grading/types";
import { persist, isConfirmed } from "./store-persist";
import { sameScriptText, scriptTextVersion } from "./script-text-version";
import { getScriptConflict } from "./script-conflict";
import { sameData } from "./same-data";
// The plan's storage lives in schedule-store beside week_date, its twin key.
// One-way import — schedule-store imports nothing from here, so no cycle — and
// both are client stores, so weekSlotsFor can read a saved plan directly.
import { getWeekPlan, finalApproverFor } from "./schedule-store";
// Filling a week's slots is pure and lives in week-slots.ts, because the server's
// submit notice must count the same slots the editor draws.
// Re-exported so the callers that have always asked this store keep asking it.
import { weekSlotsFrom, type WeekSlot, type LadderSlot } from "./week-slots";
export { weekSlotsFrom, type WeekSlot };
import { todayLocalIso } from "./schedule-dates";
import { MAX_GRADES_PER_ASK } from "./store-groups";

const overrides = new Map<string, ScriptStage>();
const listeners = new Set<() => void>();
// Snapshot cache: useSyncExternalStore requires getSnapshot to return a
// stable reference until the store actually changes.
let version = 0;

// Has a server dump of the scripts landed yet? Scripts live entirely in
// `drafts` (the clean-slate wipe emptied SCRIPTS), and that Map is filled by a
// dump of the `scripts` group: the document's seed on a page that reads them,
// or — after a client navigation from a page that does not — the poll's group
// load. Until then `findScript(id)` legitimately returns undefined for an id
// that DOES exist in the database; the root layout's <StoreGroupGate> keeps the
// page from rendering in that window.
//
// Without this flag a detail page cannot tell "not loaded yet" from "no such
// script", and calling notFound() on the former is terminal: it 404s the route
// forever, because the client never gets to hydrate and retry. That is exactly
// how /scripts/review/[id] became a dead route.
let hydrated = false;

/**
 * True once a whole dump of the `scripts` group has landed — the seed, a group
 * load, a full poll — including an empty one (a diff or a 304 says nothing
 * about a group the tab has not loaded, so neither marks it).
 */
export function isHydrated(): boolean {
  return hydrated;
}

/** Called by applyAllStores (lib/store-hydrate.ts) for every dump of `scripts`. */
export function markHydrated(): void {
  if (hydrated) return;
  hydrated = true;
  version++;
  listeners.forEach((l) => l());
}

export function getStageOverride(id: string): ScriptStage | undefined {
  return overrides.get(id);
}

export function setStageOverride(id: string, stage: ScriptStage): void {
  overrides.set(id, stage);
  persist("stage_override", id, stage);
  if (stage === "approved") markApproved("script", id);
  version++;
  listeners.forEach((l) => l());
}

// ─── When it was approved ──────────────────────────────────────────────────
//
// The Brand's status badge reads "Approved 7/13", so the day an approval landed
// has to survive the session that made it. Nothing else in the app records WHEN
// a stage moved: `store` has an updated_at column, but GET /api/store does not
// return it and it would be the wrong answer anyway — the row is rewritten by
// any later edit. So the date is its own persisted fact.
//
// A CALENDAR DATE, not a timestamp: "approved on the 13th" is a wall-clock
// statement about the person who clicked, the same argument todayLocalIso makes
// for the publish grid. Keyed `${ladder}:${id}` because the two ladders approve
// the same video independently.
//
// Approvals made before this existed have no row, and `approvedOn` answers
// undefined — the badge then reads a bare "Approved". A missing date is not a
// reason to invent one.
const approvedOnByKey = new Map<string, string>();

type Ladder = "script" | "footage";

function markApproved(ladder: Ladder, id: string): void {
  const key = `${ladder}:${id}`;
  // First approval wins — WITHIN ONE APPROVAL. `approved` is no longer terminal
  // (an admin may un-approve through the Brand lens), so this is now guarding a
  // narrower thing than it used to: a re-persist of the same standing approval,
  // not "there can only ever be one". clearApproved drops the row on the way
  // back down, so a SECOND approval after an un-approve arrives here with
  // nothing in the map and correctly stamps the day it actually happened.
  //
  // Without that pairing the badge would read the old date — "Approved 7/13" on
  // a video the Brand re-approved in August — which is the kind of wrong that
  // nobody notices until it is quoted back in a meeting.
  if (approvedOnByKey.has(key)) return;
  const iso = todayLocalIso();
  approvedOnByKey.set(key, iso);
  persist("approved_on", key, iso);
}

/**
 * Forget the approval date, because the approval it dated is being retracted.
 *
 * A TOMBSTONE, NOT A DELETE — persist(null) rather than a DELETE call, because
 * /api/store's DELETE is a two-kind allowlist that deliberately does not mirror
 * POST ("an upsert leaves a row to notice and repair, a delete leaves nothing at
 * all"). It used to have a second reason — a server-side delete never reached
 * another open tab — which the store diff retired: the poll now carries
 * deletions as well as rows.
 */
function clearApproved(ladder: Ladder, id: string): void {
  const key = `${ladder}:${id}`;
  if (!approvedOnByKey.has(key)) return;
  approvedOnByKey.delete(key);
  persist("approved_on", key, null);
}

/** The local calendar date `id` was approved on, or undefined if unrecorded. */
export function approvedOn(ladder: Ladder, id: string): string | undefined {
  return approvedOnByKey.get(`${ladder}:${id}`);
}

// ─── The undo point — what a ladder remembers about its last move ──────────
//
// One StageMove per ladder per script: where it came from, where it went, which
// action it was and which LENS fired it. Undo reads it backwards (see
// undoneScriptStage in stage-model.ts); everything else overwrites or erases it.
//
// PERSISTED, AND FOR THE SAME REASON THE APPROVAL DATE IS. An AM who approves on
// Monday and thinks better of it on Tuesday is the ordinary case, not the exotic
// one, and an undo window that closed when the tab did would be a window nobody
// could rely on. Keyed `${ladder}:${id}` exactly as approved_on is — the two
// ladders move independently and remember independently.
//
// NOT A STACK. One move deep, deliberately: the second undo in a row would be
// walking back a move somebody made before the one you are looking at, which is
// not "undo" but "rewrite the history of this video", and there is no button in
// the app that means that. Spending the record is what stops it.
const undoPoints = new Map<string, ScriptMove | FootageMove>();

/** The move that put a ladder where it is, or undefined if nothing is pending. */
export function undoPointFor(ladder: "script", id: string): ScriptMove | undefined;
export function undoPointFor(ladder: "footage", id: string): FootageMove | undefined;
export function undoPointFor(ladder: Ladder, id: string): ScriptMove | FootageMove | undefined {
  return undoPoints.get(`${ladder}:${id}`);
}

/**
 * Write down a move that may be walked back — or erase the standing record,
 * when the move that just happened is not one of those.
 *
 * THE ERASE IS THE HALF THAT IS EASY TO FORGET, and it is what keeps the record
 * honest: a creator re-uploading over a video the AM sent back must not leave
 * the AM's "Undo request edits" standing on a rung it no longer describes.
 * undoneStage's `to` check would catch that one anyway; this is the same claim
 * made where it can be read, and it covers the moves that land back on the rung
 * they started from.
 */
function recordUndoPoint(ladder: Ladder, id: string, move: ScriptMove | FootageMove | null): void {
  const key = `${ladder}:${id}`;
  if (move === null) {
    if (!undoPoints.delete(key)) return; // nothing standing — write nothing
    persist("stage_undo", key, null);    // a tombstone, for the reasons in clearApproved
    return;
  }
  undoPoints.set(key, move);
  persist("stage_undo", key, move);
}

export function effectiveStage(id: string, baseStage: ScriptStage): ScriptStage {
  return overrides.get(id) ?? baseStage;
}

/** The script's current stage, resolved from the draft row plus any override. */
export function scriptStage(id: string): ScriptStage | null {
  const entry = drafts.get(id);
  return entry ? effectiveStage(id, entry.stage) : null;
}

// ─── What became of a move somebody asked for ───────────────────────────────
//
// THESE FUNCTIONS USED TO RETURN `void`, and that is the whole bug they are
// being changed for. Each has three or four silent `return`s in it — every one a
// real, reachable answer of "no" — and a caller handed `undefined` cannot tell
// them apart from a "yes". So a Brand pressed Request edits on a stage that had
// moved under them and watched a red button do nothing at all, with no message,
// no log line, and nothing to describe to anyone afterwards.
//
// The refusals are NAMED rather than boolean because the UI owes a different
// sentence to each. "Somebody has already moved this" is a stale screen and the
// answer is to re-read the server. "The script has to be approved before a video
// can go up" is a rule the person has not met yet and the answer is to wait.
// Collapsing those into `false` would put one vague apology on both.
//
// ⚠️ REFUSAL IS NOT FAILURE. Nothing here means the server said no — these are
// this client's own stage model declining a move, decided locally, before any
// network. A write the SERVER refused is a different animal entirely and is
// handled in lib/write-failure.ts. Do not merge the two: one means "you cannot
// do that", the other means "we could not save what you did".

/**
 * `firstGradedAt` for a script graded before the field existed: the moment is
 * unknown, only the fact. The epoch, so it sorts before every real timestamp and
 * reads as what it is to anyone who ever prints one.
 */
export const GRADED_BEFORE_RECORDED = "1970-01-01T00:00:00.000Z";

/**
 * Has this script EVER been graded? The one-way gate on its first submit.
 * `grade !== null` is the fallback for a row that reached this client without
 * passing through the draft loader — a row built in memory, a test fixture —
 * and it errs open, never shut.
 */
export function hasBeenGraded(entry: Pick<ScriptEntry, "firstGradedAt" | "grade">): boolean {
  return (entry.firstGradedAt ?? null) !== null || entry.grade !== null;
}

/**
 * May this script's first submit go in? Graded once, OR submitted once — the
 * gate has to have been passed once in the script's lifetime, and a script that
 * has been submitted is past it for good, whether or not it was graded then.
 * One-way on both halves: nothing clears either stamp.
 *
 * The store's submit guard, the Submit button and the bar's "still need to
 * have checks run" line all ask this, so the three cannot disagree.
 */
export function passedGradeGate(entry: Pick<ScriptEntry, "firstGradedAt" | "firstSubmittedAt" | "grade">): boolean {
  return hasBeenGraded(entry) || (entry.firstSubmittedAt ?? null) !== null;
}

export type MoveRefusal =
  /** No such script in this client's store — a stale link, or a deleted row. */
  | "no-such-script"
  /** The transition tables have no rule for (stage, action, role). Usually: somebody moved it first. */
  | "not-allowed"
  /** A script cannot leave `approved` while its video is still standing on it. */
  | "video-standing"
  /** Footage does not exist until the script is approved. */
  | "footage-locked"
  /**
   * A first submit of a script nobody has ever graded. Only the FIRST: a
   * resubmit from `edits-requested` is never asked.
   */
  | "not-graded"
  /** A submit of a script with nothing written in it, on any rung. */
  | "blank";

export type MoveOutcome = { moved: true } | { moved: false; refusal: MoveRefusal };

const MOVED: MoveOutcome = { moved: true };
const refuse = (refusal: MoveRefusal): MoveOutcome => ({ moved: false, refusal });

/**
 * WHICH TABLE THIS SCRIPT'S MOVES ARE READ OUT OF — its brand's seat.
 *
 * ⚠️ LOOKED UP HERE RATHER THAN PASSED IN, and that is the reason this change
 * touched almost nothing. Every entry point below already has a script id, and a
 * draft already carries `brandSlug`, so the seat is derivable at the guard
 * itself — which means not one caller in the app has to learn that brands differ
 * about this. A `finalApprover` argument on applyScriptAction would have put the
 * question in every button, every modal and every test that fires a move, and
 * the first one to answer it wrong would have granted an approval nobody gave.
 *
 * A script whose brand nobody has configured — or that has no draft row at all,
 * which the callers refuse a moment later anyway — gets the default seat, the
 * two-seat ladder. See finalApproverFor.
 */
function seatFor(id: string): FinalApprover {
  return finalApproverFor(drafts.get(id)?.brandSlug ?? "");
}

/**
 * Fire a role's action on a script, or do nothing at all — and say which.
 *
 * THIS IS THE GUARD, not the button's visibility. Every UI path runs through
 * here, so a creator firing an AM's Approve returns null from nextScriptStage
 * and dies in the store — not merely in the render. An impossible transition is
 * impossible, rather than only unrendered.
 *
 * A rejected action writes NOTHING: no stage, no persist, no version bump, no
 * notify. A silent persist of an unchanged value would still echo to the server.
 * What it does now do is TELL THE CALLER, so that "nothing happened" is a
 * sentence somebody reads rather than a thing they have to infer from a screen
 * that did not change.
 */
export function applyScriptAction(id: string, action: ScriptAction, role: Role): MoveOutcome {
  const from = scriptStage(id);
  if (from === null) return refuse("no-such-script");

  // TWO SOURCES FOR ONE `to`, and only one of them is the table. A forward move
  // is a function of (stage, action, role); an undo is a function of what this
  // ladder remembers, and both the "is it yours" and the "has anyone acted
  // since" tests live inside undoneScriptStage. Everything below this line
  // treats them identically, which is the point — an undo is a stage move like
  // any other once it has been allowed.
  const seat = seatFor(id);
  const to = action === "undo"
    ? undoneScriptStage(undoPointFor("script", id), from, role, footageStage(id))
    : nextScriptStage(from, action, role, seat);
  if (to === null) return refuse("not-allowed"); // the model forbids it

  // THE SUBMIT GATE, AND THE BUTTON IS ONLY ITS NOTICE. Asked after the table,
  // so a move nobody may make still reads as "not allowed" rather than as
  // advice about grading. Blank blocks every rung; the grade only the first
  // submit — "once it's been submitted, you're in", so a script that went in
  // ungraded through the old batch submit is never stuck.
  if (action === "submit") {
    const entry = drafts.get(id);
    if (entry && isBlankFields(fieldsFromEntry(entry))) return refuse("blank");
    if (from === "to-be-submitted" && entry && !passedGradeGate(entry)) return refuse("not-graded");
  }

  // The one rule the transition table cannot express, because it is a fact
  // about the OTHER ladder: a script cannot leave `approved` while its video is
  // still standing on it. See canUnapproveScript. (undoneScriptStage asks this
  // too, for the render; a forward move out of `approved` has no rule today and
  // this is what keeps that true if one is ever added.)
  if (from === "approved" && !canUnapproveScript(footageStage(id))) return refuse("video-standing");

  if (action !== "undo") trackWorkflow("started", id, {
    workflow: action === "submit" ? "script_submission" : "script_review",
    outcome: action === "submit" ? "submitted" : action === "approve" ? "approved" : "edits_requested",
  });
  setStageOverride(id, to);
  if (from === "approved" && to !== "approved") clearApproved("script", id);
  // A SUBMIT, OR A SUBMIT TAKEN BACK, is proof this script has been over the
  // gate. The undo half is the one that matters today: a script that went in
  // ungraded through the old batch submit has no stamp, and its first trip back
  // to `to-be-submitted` is where it earns one — rather than being asked for a
  // grade it never needed (passedGradeGate).
  if (action === "submit" || to === "to-be-submitted") markSubmittedOnce(id);
  recordUndoPoint("script", id,
    action !== "undo" && scriptMoveIsUndoable(from, action, role, seat) ? { from, to, action, role } : null);
  return MOVED;
}

/** The footage twin of applyScriptAction. Same contract. */
export function applyFootageAction(id: string, action: FootageAction, role: Role): MoveOutcome {
  // Footage does not exist until the script is approved, so an action on
  // locked footage is not merely illegal — there is nothing to act on. The two
  // are told apart because a Brand looking at a video that has vanished from
  // under them needs a different sentence than one whose link was never valid.
  const script = scriptStage(id);
  if (script === null) return refuse("no-such-script");
  if (!footageUnlocked(script)) return refuse("footage-locked");
  const from = footageStage(id);
  const seat = seatFor(id);
  const to = action === "undo"
    ? undoneFootageStage(undoPointFor("footage", id), from, role)
    : nextFootageStage(from, action, role, seat);
  if (to === null) return refuse("not-allowed"); // the model forbids it — write nothing

  if (action === "approve" || action === "request-edits") trackWorkflow("started", id, {
    workflow: "video_review", outcome: action === "approve" ? "approved" : "edits_requested",
  });
  // REMOVING IS RETRACTING, so the video goes with the stage. A stage that says
  // "To be uploaded" beside a Drive embed still playing last week's cut is the
  // worst of both — the creator believes they withdrew it, and the AM can still
  // watch it.
  //
  // BEFORE moveFootage, not after, because moveFootage is what notifies. Done
  // afterwards, subscribers would be woken by a store holding the new stage and
  // the old video — a state that is never true and that nothing downstream is
  // written to expect. Both halves of the retraction land, then the app is told.
  if (action === "remove") {
    driveVideos.delete(id);
    persist("drive_video", id, null); // a tombstone, for the reasons in clearApproved
  }
  moveFootage(id, to);
  recordUndoPoint("footage", id,
    action !== "undo" && footageMoveIsUndoable(from, action, role, seat) ? { from, to, action, role } : null);
  return MOVED;
}

export function subscribeStages(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getStagesVersion(): number {
  return version;
}

// ─── LLM grade records ───────────────────────────────────────────────────────
// One GradeRecord per script, store kind "grade", written server-side by
// /api/grade — the client never persists these itself except a null tombstone
// (deleteScript). NOT SEEDED: the editor asks for the ones it shows
// (loadGradeRecord, below), the poll's diff keeps them current, and
// applyGradeRecord echoes a grade the editor watched land (POST /api/grade
// answers with a job; its route answers the record).
// Shares the version/listener channel.

const gradeRecords = new Map<string, ClientGradeRecord>();

/**
 * A grade as the browser holds it: the record without the text it was given
 * (`fieldSnapshot`) and that text's hashes (`fieldHashes`). Both are read only
 * by the server's regrade, which reads the full row, so the client dump leaves
 * them out (getAllRows) — and the type says so, so that a client reader of
 * either is a compile error rather than an `undefined`.
 */
export type ClientGradeRecord = Omit<GradeRecord, "fieldSnapshot" | "fieldHashes"> & { brandRequirementsAvailable?: boolean; brandDocumentsChanged?: boolean };

/**
 * The scripts somebody asked to grade IN THIS TAB — the rail opens its notes on
 * these and on nothing else, so a reload lands on a collapsed score instead of
 * re-expanding last session's reading.
 *
 * applyGradeRecord is the only writer, and it is called on exactly the one "a
 * grade was just made in front of this tab" path: the rail's grade job landing —
 * pressed here, or, since grades became jobs, found running when the rail loaded
 * and watched to its end. (The status bar's batch grade was the other, until
 * scripts started going in one at a time.)
 * The poll's diff and the editor's GET (loadGradeRecord) write gradeRecords
 * directly, so a record that merely ARRIVES never counts as one you asked for.
 */
const gradedThisSession = new Set<string>();

export function gradeRecordFor(id: string): ClientGradeRecord | null {
  return gradeRecords.get(id) ?? null;
}

/** Did this tab ask for the grade it is now showing for `id`? */
export function wasGradedThisSession(id: string): boolean {
  return gradedThisSession.has(id);
}

/** Local echo of a record a grade job just landed — the poll would bring the
 *  same row within 10s; this makes the UI move now. */
export function applyGradeRecord(record: GradeRecord): void {
  // Held in the dump's shape, so the poll that brings the same grade finds it
  // equal and wakes nobody a second time.
  const { fieldSnapshot, fieldHashes, ...shown } = record;
  gradeRecords.set(record.scriptId, shown);
  gradedThisSession.add(record.scriptId);
  touchGrade(record.scriptId);
  version++;
  listeners.forEach((l) => l());
}

/**
 * Local half of the admin grade reset (/api/grade/reset deletes the row).
 *
 * Both halves are needed: the server's deletion reaches every OTHER tab on its
 * next poll (the diff's `deleted`), but this one should not keep rendering the
 * grade for up to ten seconds after its own Reset.
 *
 * gradedThisSession is cleared too. It means "somebody asked for this grade in
 * this tab", and after a reset nobody has — leaving it set would make the next
 * grade's panel auto-open as if it were a fresh answer to a question that was
 * thrown away.
 */
export function clearGradeRecord(scriptId: string): void {
  gradeRecords.delete(scriptId);
  gradedThisSession.delete(scriptId);
  touchGrade(scriptId);
  version++;
  listeners.forEach((l) => l());
}

// ─── Which grades this tab is current on, and asking for the rest ───────────
//
// `grade` left the seed and the full dump, where it was 1.86 MB of every hard
// load, so an absent record no longer means "never graded". It means that only
// for a script this tab is CURRENT ON, and the editor must not say "Not graded
// yet" for any other: it shows a loading state and asks.
//
// The tab is current on a script's grade once any of these has spoken for it:
//
//   - the editor's GET (loadGradeRecord), a record or a null;
//   - a diff row or deletion for the key (applyServerRows): a diff carries the
//     row as it stands, and every later diff on the same cursor carries any
//     change after it;
//   - this tab's own grade, reset or delete (applyGradeRecord,
//     clearGradeRecord, deleteScript);
//   - minting the draft (newDraftEntry): an id never used before has no grade.
//
// And it stops being current on all of them at once when a FULL answer lands
// (forgetGradeKnowledge, from applyAllStores): a full answer carries no
// grades, and it moves the cursor past whatever grade writes the tab missed —
// a restart's gap, or a heal. The records held stay on screen; the editor
// asks again for the ones it shows, and the answer corrects them.
//
// ⚠️ A GET ANSWER NEVER OVERWRITES A NEWER WORD. A diff applied while the GET
// was in flight may be newer than what the GET read, and nothing would ever
// send it again (the cursor is past it). So each key carries a touch count,
// bumped by every source above; an answer whose key was touched after it was
// asked for is dropped, and the key keeps what the newer source said. The
// reverse — the GET newer than a diff that lands after it — heals itself: the
// write the GET saw has a seq the next diff still owes this tab.

const gradeKnown = new Set<string>();
const gradeScoped = new Set<string>();
const gradeFailed = new Set<string>();
/**
 * Grades still being made, script id → job id, as the editor's GET last said (a
 * grade is a job the page polls). How an editor reloaded mid-grade knows to show
 * it running and poll it rather than offer a button. The editor forgets an entry
 * when it has seen the job finish.
 */
const gradeJobs = new Map<string, string>();
const gradeAsked = new Set<string>();
const gradeTouches = new Map<string, number>();
let gradeQueue: string[] = [];

/** Something spoke for this key's grade: the tab is current on it, and any GET still in flight for it is older. */
function touchGrade(id: string): void {
  gradeTouches.set(id, (gradeTouches.get(id) ?? 0) + 1);
  gradeKnown.add(id);
  gradeFailed.delete(id);
}

/**
 * Where the grade for `id` stands in this tab. `"ready"`: gradeRecordFor(id)
 * is the truth, a null included. `"loading"`: not current on it yet, so a null
 * from gradeRecordFor means "not here yet", not "never graded"; a held record
 * may still be shown while it is asked for again. `"failed"`: the last ask
 * failed, and nobody will ask again until retryGradeRecord.
 */
export type GradeLoad = "ready" | "loading" | "failed";

/** The grade job the editor's GET said is running for `id`, or null. */
export function runningGradeJobFor(id: string): string | null {
  return gradeJobs.get(id) ?? null;
}

/** The editor has seen this grade job finish (or started watching another). */
export function forgetGradeJob(id: string): void {
  if (!gradeJobs.delete(id)) return;
  version++;
  listeners.forEach((l) => l());
}

export function gradeLoadFor(id: string): GradeLoad {
  if (gradeKnown.has(id)) return "ready";
  return gradeFailed.has(id) ? "failed" : "loading";
}

/**
 * A full server answer landed (lib/store-hydrate.ts): it said nothing about
 * grades, and the tab's cursor moved past any it missed. Keep the records,
 * forget being current on them, so the editor asks again for what it shows.
 */
export function forgetGradeKnowledge(): void {
  if (gradeKnown.size === 0 && gradeFailed.size === 0) return;
  gradeKnown.clear();
  gradeFailed.clear();
  version++;
  listeners.forEach((l) => l());
}

/**
 * Ask the server for this script's grade, unless the tab is current on it, it
 * is already being asked for, or the last ask failed (retryGradeRecord).
 *
 * Batched: every ask made in one tick goes out as ONE request, so a week's
 * five rows mounting together cost one GET, not five.
 */
export function loadGradeRecord(id: string): void {
  const needsScopedRead = !gradeScoped.has(id) && gradeRecords.get(id)?.brandRequirementsAvailable;
  gradeScoped.add(id);
  if ((gradeKnown.has(id) && !needsScopedRead) || gradeAsked.has(id) || gradeFailed.has(id)) return;
  gradeAsked.add(id);
  gradeQueue.push(id);
  if (gradeQueue.length === 1) queueMicrotask(sendGradeAsks);
}

/** Recheck document metadata when an editor opens or regains focus; never grades. */
export function refreshGradeRecord(id: string): void {
  gradeKnown.delete(id);
  gradeFailed.delete(id);
  loadGradeRecord(id);
}

/**
 * The editor's "Try again" after a failed ask — for EVERY grade that failed,
 * not only the row whose button was pressed. The week's rows share one
 * request, so one dropped request fails all of them at once, and a Try again
 * that fixed one row would leave the person pressing it down the page (found
 * on the walk). Each comes back in one batched request.
 */
export function retryGradeRecord(id: string): void {
  if (!gradeFailed.has(id)) return;
  const again = [...gradeFailed];
  gradeFailed.clear();
  again.forEach(loadGradeRecord);
  version++;
  listeners.forEach((l) => l());
}

function sendGradeAsks(): void {
  const ids = gradeQueue;
  gradeQueue = [];
  for (let i = 0; i < ids.length; i += MAX_GRADES_PER_ASK) void askForGrades(ids.slice(i, i + MAX_GRADES_PER_ASK));
}

async function askForGrades(ids: string[]): Promise<void> {
  const asked = new Map(ids.map((id) => [id, gradeTouches.get(id) ?? 0]));
  let grades: Record<string, ClientGradeRecord | null> | null = null;
  let running: Record<string, string> = {};
  try {
    const res = await fetch(`/api/scripts/editor/grade?${ids.map((id) => `scriptId=${encodeURIComponent(id)}`).join("&")}`, { cache: "no-store" });
    if (res.ok) {
      const body = (await res.json()) as { grades?: Record<string, ClientGradeRecord | null>; running?: Record<string, string> };
      grades = body.grades ?? null;
      running = body.running ?? {};
    }
  } catch {
    // grades stays null: every id below is marked failed, and the editor offers Try again.
  }
  for (const id of ids) {
    gradeAsked.delete(id);
    // Spoken for by something newer while this was in flight (the header).
    if ((gradeTouches.get(id) ?? 0) !== asked.get(id)) {
      const current = gradeRecords.get(id);
      const scoped = grades?.[id];
      // A same-run broadcast cannot carry document feedback or freshness.
      // Accept only those scoped fields; never replace a newer score or run.
      if (current?.runId && scoped && scoped.runId === current.runId) {
        const { brandRequirements: _requirements, brandDocumentsChanged: _changed, ...rest } = current;
        upsert(gradeRecords, id, { ...rest,
          ...(scoped.brandRequirements ? { brandRequirements: scoped.brandRequirements } : {}),
          ...(scoped.brandDocumentsChanged ? { brandDocumentsChanged: true } : {}),
        });
      }
      continue;
    }
    if (grades === null || !(id in grades)) {
      gradeFailed.add(id);
      continue;
    }
    const data = grades[id];
    touchGrade(id);
    if (running[id]) gradeJobs.set(id, running[id]);
    // This tab's own grade write still in the air (deleteScript's tombstone)
    // outranks the server's copy, exactly as it does over a polled row.
    if (!isConfirmed("grade", id, data)) continue;
    if (data === null) {
      gradeRecords.delete(id);
      gradedThisSession.delete(id);
    } else upsert(gradeRecords, id, data);
  }
  version++;
  listeners.forEach((l) => l());
}

// ─── Footage tracking — v0.2.3, rewritten v0.4.0, re-staged v0.4.1 ─────────
// Post-approval workflow: creator pastes a Drive link (a valid paste IS the
// upload, see recordDriveVideo) → AM approves → brand approves. There is no
// `posted` rung any more: nothing surviving renders views or link clicks, so
// nothing could ever have moved a video off it.
//
// The QC self-report is gone. It scored a file the picker had already thrown
// away — /scripts/production kept file.name and dropped the bytes — so its
// "grade" described nothing. Shares the same version/listener channel as
// script stages, so one subscription covers both.
//
// Every transition here runs through nextFootageStage. An illegal move returns
// null and is dropped: no write, no persist, no version bump, no notify. The
// guard is the model, not the caller.

const footageStages = new Map<string, FootageStage>();

/** Commit a footage move, or do nothing at all if the model forbids it. */
function moveFootage(id: string, to: FootageStage | null): void {
  if (to === null) return;
  const from = footageStage(id);
  footageStages.set(id, to);
  persist("video_stage", id, to);
  if (to === "approved") markApproved("footage", id);
  // Leaving `approved` can only be an un-approve, and the date described the
  // approval being retracted. Paired with markApproved above so a re-approval
  // stamps the day it really happened.
  else if (from === "approved") clearApproved("footage", id);
  version++;
  listeners.forEach((l) => l());
}

/** AM approves the uploaded footage — only valid once footage is actually up. */
export function approveVideoAsAm(id: string): void {
  moveFootage(id, nextFootageStage(footageStage(id), "approve", "am", seatFor(id)));
}

/** Brand approves — only valid once the AM has already cleared it. */
export function approveVideoAsBrand(id: string): void {
  moveFootage(id, nextFootageStage(footageStage(id), "approve", "brand", seatFor(id)));
}

// ─── Drive footage — the real upload path ────────────────────────────────────
// A creator pastes a Drive link; a valid paste IS the upload. Script-keyed, so
// it lives here rather than in the schedule store.

export interface DriveVideo {
  fileId: string;
  driveUrl: string;
  name: string;
  access: "public" | "restricted" | "public-not-ours";
  /** Absent if Drive was still processing the upload when the link was pasted. */
  widthPx?: number;
  heightPx?: number;
  checkedAt: number;
  addedAtMs: number;
}

const driveVideos = new Map<string, DriveVideo>();

export function getDriveVideo(scriptId: string): DriveVideo | undefined {
  return driveVideos.get(scriptId);
}

/**
 * A valid paste IS the upload — there is no QC self-report gate, the old one
 * graded a file the picker had already thrown away. Persists BOTH rows: the
 * video and the stage.
 *
 * Uploading is the creator's `upload` action, so it runs through the model like
 * every other transition: `to-be-uploaded` and `edits-requested` both land on
 * `awaiting-am-review`, `awaiting-am-review` self-loops (a re-upload while the
 * AM is still looking — same queue, new file), and anything else no-ops. A
 * creator STILL cannot re-upload out from under a brand review; that is the one
 * rung the self-loop deliberately does not extend to. 2.5 adds the remaining
 * guard — that the script itself is approved.
 *
 * The overwrite is the whole re-upload: one `drive_video` row per script, so
 * `set` replaces the old file and the persist replaces the old row. Nothing has
 * to remember the previous link, because nothing may go back to it — the
 * creator's own re-paste is the only way the row ever changes.
 */
export function recordDriveVideo(scriptId: string, video: DriveVideo): MoveOutcome {
  // Footage cannot exist before its script is approved. Without this, a
  // paste against ANY id advances the footage: footageStage() answers
  // "to-be-uploaded" for scripts that have never been approved, and for scripts
  // that do not exist at all.
  //
  // THREE WAYS TO SAY NO, AND THEY USED TO BE BARE `return`s. The modal
  // above this called it, closed itself and reported success regardless — so a
  // creator pasted a link that had just passed every Drive check we have, watched
  // the box close, and got no video and no explanation. Each refusal now comes
  // back named, and upload-footage-modal.tsx keeps the box open with the reason
  // on it.
  const script = scriptStage(scriptId);
  if (script === null) return refuse("no-such-script");
  // THIS SCRIPT'S approval is the whole gate. Filming used to wait for every
  // script in the week (decided 2026-07-10, the `week-locked` refusal); each
  // video now films as soon as its own script is approved.
  if (!footageUnlocked(script)) return refuse("footage-locked");

  // The seat, even here: `upload` is the creator's own and reads the same out of
  // either table, but it is read out of a table, and there is exactly one way to
  // choose one.
  const to = nextFootageStage(footageStage(scriptId), "upload", "creator", seatFor(scriptId));
  if (to === null) return refuse("not-allowed"); // no video row either: the upload did not happen

  trackWorkflow("started", scriptId, { workflow: "video_upload", outcome: "uploaded" });
  driveVideos.set(scriptId, video);
  footageStages.set(scriptId, to);
  persist("drive_video", scriptId, video);
  persist("video_stage", scriptId, to);
  // An upload is not undoable (Remove is what takes it back), so it leaves no
  // undo point — and it clears whatever was standing. This is the same clause
  // applyFootageAction ends on; the upload path just does not go through it.
  recordUndoPoint("footage", scriptId, null);
  version++;
  listeners.forEach((l) => l());
  return MOVED;
}

/** The persisted row kind is still `video_stage`; only the vocabulary moved. */
export function footageStage(id: string): FootageStage {
  return footageStages.get(id) ?? "to-be-uploaded";
}

// ─── Comment threads — v0.4.0 ────────────────────────────────────────────────
// The comment section stays open for clients, so they can let us know anything
// they need to.
//
// FOUR threads per script: {script, video} x {creator-am, am-client}. There
// used to be one flat thread shared by every role.
//
// `audience` is PRESENTATIONAL, not a security boundary. GET /api/store still
// dumps every row to every signed-in client, so a creator's browser holds the
// am-client rows and always will. Anyone porting this must design enforcement
// fresh rather than assume this field carries it.

export type CommentSubject = "script" | "video";
export type CommentAudience = "creator-am" | "am-client";

// A comment may point at a span of one read-only DocSection.
// OPTIONAL end to end: an absent anchor is an ordinary thread note, exactly as
// before. `quote` is the durable record (a copy of the selected text, so it can
// never go stale); start/end are a best-effort locator that drifts as the field
// is edited and is re-resolved live against the current text (lib/comment-anchor).
export interface CommentAnchor {
  /** The read-only DocSection id the span lives in: "doc-hook", "doc-script", … */
  field: string;
  /** Exact selected substring, frozen at post time. The DURABLE record. */
  quote: string;
  /** Char offsets into that field's text at post time. Best-effort; drifts. */
  start: number;
  end: number;
}

export interface Comment {
  id: string; // uuidv7 — time-ordered, so it self-sorts chronologically
  scriptId: string;
  subject: CommentSubject;
  audience: CommentAudience;
  /** The signed-in email. Comparing display names breaks the moment two people share one. */
  authorId: string;
  authorName: string; // "Jonathan Chu"
  // The role the author actually HOLDS, e.g. "Admin (full access)" — never the
  // lens they happened to be looking through when they wrote it (see
  // persona-context.tsx). Tooltip only.
  authorRole: string;
  text: string;
  /** Replaces a display string frozen at post time in the poster's own timezone. */
  postedAtMs: number;
  /** Presence renders "(edited)". */
  editedAtMs?: number;
  /** Tombstone, kept in the row so the text survives for audit (deleteComment). */
  deleted: boolean;
  /** Optional text anchor. Absent = a plain thread note. */
  anchor?: CommentAnchor;
}

export interface NewComment {
  scriptId: string;
  subject: CommentSubject;
  audience: CommentAudience;
  authorId: string;
  authorName: string;
  authorRole: string;
  text: string;
  anchor?: CommentAnchor;
}

// Keyed by comment id (was: scriptId -> Comment[]). One row per comment so
// concurrent appends from different roles merge instead of clobbering.
const comments = new Map<string, Comment>();

function writeComment(c: Comment): void {
  comments.set(c.id, c);
  persist("comment", c.id, c); // one row; never overwrites another
  version++;
  listeners.forEach((l) => l());
}

/**
 * Throws on an empty authorId, loudly and always.
 *
 * If session.userEmail is ever undefined, authorId becomes "" on every comment
 * — and then canModifyComment matches everyone against everyone, handing every
 * user an edit/delete kebab on every card. Silent AND permissive, the worst
 * pair. Refusing the write is the only place this can be stopped cheaply.
 */
export function addComment(input: NewComment): void {
  if (!input.authorId) throw new Error("addComment: authorId is required");
  if (!input.text.trim()) return;
  writeComment({
    id: uuidv7(),
    scriptId: input.scriptId,
    subject: input.subject,
    audience: input.audience,
    authorId: input.authorId,
    authorName: input.authorName,
    authorRole: input.authorRole,
    text: input.text.trim(),
    postedAtMs: Date.now(),
    deleted: false,
    anchor: input.anchor, // optional; undefined for a plain note (editComment preserves it via spread)
  });
}

export function editComment(id: string, text: string): void {
  const existing = comments.get(id);
  if (!existing || existing.deleted || !text.trim()) return;
  writeComment({ ...existing, text: text.trim(), editedAtMs: Date.now() });
}

/**
 * A tombstone, not a delete: /api/store's DELETE refuses `comment`, and the
 * body is retained: a comment can only be deleted after everyone in
 * the thread already saw it, so scrubbing the text would guard only against
 * someone who missed it live and later opens devtools. Retention is audit.
 */
export function deleteComment(id: string): void {
  const existing = comments.get(id);
  if (!existing || existing.deleted) return;
  writeComment({ ...existing, deleted: true });
}

/** One thread. Filters all three keys and drops tombstones. */
export function getComments(
  scriptId: string,
  subject: CommentSubject,
  audience: CommentAudience,
): Comment[] {
  return Array.from(comments.values())
    .filter((c) => c.scriptId === scriptId && c.subject === subject && c.audience === audience && !c.deleted)
    .sort((a, b) => a.id.localeCompare(b.id)); // uuidv7 is time-ordered
}

// ─── Draft creation — v0.3.1, new ───────────────────────────────────────────
// Fixes a real gap: SCRIPTS is a static seed array, so there was no way for
// a creator to start a genuinely blank script — every entry point into the
// editor (sidebar link, "Open Script Editor" CTA) fell back to one hardcoded
// demo script (JC-OI-W3-1, Jay Crotty's) regardless of who was logged in.
// This gives each creator their own in-memory blank draft, same
// resets-on-reload architecture as everything else in this store.

const drafts = new Map<string, ScriptEntry>();
let draftCounter = 0;

/**
 * The labels a new draft is stamped with. The KEYS (creatorEmail, and the slug
 * inside this) are ids; these are the denormalized display copies that ride on
 * the row so a list can render without a roster lookup.
 */
export type { DraftOwner } from './script-draft';
import { buildScriptDraft, type DraftOwner } from './script-draft';

/**
 * Where a draft's labels come from: the creator's own existing scripts first,
 * and only then the caller's fallback.
 *
 * The scripts win because they are the truth about which brand this creator is
 * actually writing for. The fallback exists for the one case they cannot answer
 * — a creator with no scripts at all, whose very first draft would otherwise be
 * born with no brand — and there the caller passes the persona's assignment.
 *
 * AN EXPLICIT brand from the caller WINS, because a multi-brand creator minting a
 * draft in one brand's week must not have it stamped with another's — the caller
 * (the editor, the Videos page) knows the week's brand and says so. Only when the
 * caller names none is the brand inferred, and only when the creator's scripts
 * agree on ONE; two brands and no explicit slug leaves it empty rather than
 * guessing. The creator's NAME and handle still come from their existing scripts
 * either way — those are the creator's, not the brand's.
 */
function ownerFor(existing: ScriptEntry[], owner?: DraftOwner): Required<DraftOwner> {
  const slugs = Array.from(new Set(existing.map((s) => s.brandSlug).filter(Boolean)));
  const inferredSlug = slugs.length === 1 ? slugs[0] : "";
  const slug = owner?.brandSlug || inferredSlug;
  const fromBrand = slug ? existing.find((s) => s.brandSlug === slug) : undefined;

  return {
    creatorName: existing.find((s) => s.creatorName)?.creatorName ?? owner?.creatorName ?? "",
    creatorHandle: existing.find((s) => s.creatorHandle)?.creatorHandle ?? owner?.creatorHandle ?? "",
    brandSlug: slug,
    brandName: fromBrand?.brandName ?? owner?.brandName ?? "",
  };
}

/** A draft id fragment from an email. Ids are opaque; this only has to be stable and legible. */
const draftKey = (creatorEmail: string) =>
  creatorEmail.replace(/@.*$/, "").replace(/[^a-zA-Z0-9]+/g, "-").toUpperCase();

/**
 * A brand-new draft row for a slot — THE ONE PLACE A DRAFT IS BORN, and the
 * reason the three functions below cannot drift apart. It was three copies of
 * the same twenty-line object literal until an import needed a fourth.
 *
 * It takes the caller's `fields` and spreads them in LAST, so the row arrives
 * already holding whatever text it is meant to hold. That is what makes a single
 * write possible — see mintDraftWithFields.
 *
 * ⚠️ `DraftFields`, NOT `ScriptFields`, and the two left out are the reason. A
 * ScriptFields carries `brandName` — the name the CREATOR TYPED into their
 * document — and spreading one here would overwrite the ROW's brand with it.
 *
 * It bumps the counter, and that is the only thing it does besides build: it
 * does not store the row and does not persist it, so a caller that decides not
 * to keep it has burned an id and nothing else.
 */
function newDraftEntry(
  creatorEmail: string,
  week: number,
  videoNum: number,
  owner?: DraftOwner,
  fields?: Partial<DraftFields>,
): ScriptEntry {
  const { creatorName, creatorHandle, brandSlug, brandName } = ownerFor(scriptsForCreator(creatorEmail), owner);
  draftCounter += 1;
  const id = `DRAFT-${draftKey(creatorEmail)}-${draftCounter}`;
  // A NEW ID HAS NO GRADE, and this tab knows it without asking: a draft id is
  // never minted twice (deleteScript's tombstone keeps it spoken for), so the
  // editor row that minted it says "Not graded yet" rather than loading.
  touchGrade(id);
  return buildScriptDraft(id, creatorEmail, week, videoNum, { creatorName, creatorHandle, brandSlug, brandName }, fields);
}

// THE STATIC SEED ARRAY IS GONE. These four readers used to union `SCRIPTS` —
// a hardcoded array of demo scripts — with the live drafts. The clean-slate wipe
// emptied SCRIPTS and every script became a `draft` row, so the union had been
// concatenating an array that is guaranteed empty ever since: dead branches on
// the hottest read paths in the app, each one implying a second source of scripts
// that no longer exists. `drafts` is the only source now.

/** Existing scripts for a creator. Empty until hydration. */
export function scriptsForCreator(creatorEmail: string): ScriptEntry[] {
  return Array.from(drafts.values()).filter((s) => s.creatorEmail === creatorEmail);
}

/** Existing scripts for a brand. Empty until hydration. */
export function scriptsForBrand(brandSlug: string): ScriptEntry[] {
  return Array.from(drafts.values()).filter((s) => s.brandSlug === brandSlug);
}

/** Every script the app knows about. Empty until hydration. */
export function allScripts(): ScriptEntry[] {
  return Array.from(drafts.values());
}

/** Distinct creator IDS across every known script. Empty until hydration. */
export function creatorEmails(): string[] {
  return Array.from(new Set(allScripts().map((s) => s.creatorEmail)));
}

/** Distinct brand SLUGS across every known script. Empty until hydration. */
export function brandSlugs(): string[] {
  return Array.from(new Set(allScripts().map((s) => s.brandSlug)));
}

/**
 * Looks up a script by id.
 *
 * A miss means "no such script" ONLY for a caller rendering under <StoreSeed>,
 * which warms this map during its render body in the root layout — that covers
 * every page. Anywhere else (a caller that runs before the seed, or outside the
 * layout) a miss may just mean "not loaded yet", and treating it as absence is
 * what killed /scripts/review/[id]; see the header comment. Check isHydrated()
 * if you are not sure which of the two you are.
 */
export function findScript(id: string): ScriptEntry | undefined {
  return drafts.get(id);
}

/**
 * Creates a blank draft for a creator's next video slot and returns it.
 * Idempotent: if the creator already has an open (draft/revision-requested)
 * script — including one created by an earlier call — that's returned
 * instead of minting a second one. Safe to call from render, since a
 * repeat call for the same creator is a no-op read, not a new mutation.
 */
export function createDraftScript(creatorEmail: string, owner?: DraftOwner): ScriptEntry {
  const alreadyOpen = openScriptsFor(creatorEmail);
  if (alreadyOpen.length > 0) return alreadyOpen[0];

  const existing = scriptsForCreator(creatorEmail);
  const week = existing.length > 0 ? Math.max(...existing.map((s) => s.week)) : 1;
  const thisWeek = existing.filter((s) => s.week === week);
  const full = thisWeek.length >= 5;
  const targetWeek = full ? week + 1 : week;
  const videoNum = full ? 1 : thisWeek.length + 1;

  const entry = newDraftEntry(creatorEmail, targetWeek, videoNum, owner);
  drafts.set(entry.id, entry);
  persist("draft", entry.id, entry);
  version++;
  listeners.forEach((l) => l());
  return entry;
}

// ─── Script content on the draft row — v0.3.2 ────────────────────────────────
// After the clean-slate wipe every script is a mutable draft, so content lives
// on the draft entry itself — no separate override layer. saveDraftFields edits
// it in place and re-persists that one row.

/** Matches the seed's `lastGraded` format, e.g. "Jul 8". */
function gradedTodayLabel(): string {
  return new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Write a grade back onto the draft row.
 *
 * Until this existed, grading was ephemeral: ScriptEditorCanvas dropped
 * gradeScript()'s result into local React state and nothing persisted it, so
 * `entry.grade` stayed null for every script a creator wrote — non-null only on
 * the seeded ones. Everything that reads a grade (the bar's stat lines, the
 * editor's slot dots) was therefore reading a field that could never be set.
 *
 * Regrading OVERWRITES. gradeScript is pure over ScriptFields, so a regrade is
 * deterministic and there is nothing to preserve.
 */
export function saveGrade(id: string, result: Pick<GradeResult, "grade" | "trafficLight">): void {
  const entry = drafts.get(id);
  if (!entry) return;
  const next: ScriptEntry = {
    ...entry,
    grade: result.grade,
    trafficLight: result.trafficLight,
    lastGraded: gradedTodayLabel(),
    // ONCE. A regrade keeps the first moment: this is the gate on the first
    // submit, not a record of the latest grade.
    firstGradedAt: entry.firstGradedAt ?? new Date().toISOString(),
  };
  drafts.set(id, next);
  persist("draft", id, next); // whole draft row — same shape as saveDraftFields
  version++;
  listeners.forEach((l) => l());
}

/**
 * Stamp `firstSubmittedAt`, once. A no-op on a script that already carries it,
 * so a resubmit writes nothing — the draft row is only persisted the first time.
 */
function markSubmittedOnce(id: string): void {
  const entry = drafts.get(id);
  if (!entry || (entry.firstSubmittedAt ?? null) !== null) return;
  const next: ScriptEntry = { ...entry, firstSubmittedAt: new Date().toISOString() };
  drafts.set(id, next);
  persist("draft", id, next);
  version++;
  listeners.forEach((l) => l());
}

/**
 * The draft row's half of the admin grade reset — put the grade back to none.
 * saveGrade's mirror, BUT NOT FOR `firstGradedAt`: having been graded once is a
 * one-way gate, so the reset clears the number and leaves the script free to
 * submit. It is spread through untouched.
 *
 * The numeric grade is denormalized onto the draft on purpose (the roster, the
 * status bar and its stat lines all read it rather than the GradeRecord),
 * so deleting the record alone would leave those three surfaces quoting a
 * score with nothing behind it. "Reset" has to mean reset everywhere it shows.
 */
export function clearSavedGrade(id: string): void {
  const entry = drafts.get(id);
  if (!entry) return;
  const next: ScriptEntry = { ...entry, grade: null, trafficLight: null, lastGraded: null };
  drafts.set(id, next);
  persist("draft", id, next);
  version++;
  listeners.forEach((l) => l());
}

/**
 * Merge edited fields into a draft entry and persist it. No-op if not a draft.
 *
 * Returns the durable write. Callers that only need the next render can ignore
 * it — the Map above is already authoritative. A caller that is about to ask
 * the SERVER to read this row must await it: see requestGrade in the editor.
 */
export function saveDraftFields(id: string, fields: Partial<ScriptFields>): Promise<void>;
export function saveDraftFields(id: string, fields: Partial<ScriptFields>, options: { expectedTextVersion: number }): Promise<{ saved: boolean; entry?: ScriptEntry }>;
export function saveDraftFields(id: string, fields: Partial<ScriptFields>, options?: { expectedTextVersion: number }): Promise<void | { saved: boolean; entry?: ScriptEntry }> {
  const entry = drafts.get(id);
  if (!entry || getScriptConflict(id)) return Promise.resolve(options ? { saved: false } : undefined);
  const { brandName, creatorName: _creatorName, ...text } = fields;
  const next = { ...entry, ...text, ...(brandName !== undefined ? { brandNameProvided: brandName } : {}) };
  const expected = options?.expectedTextVersion ?? scriptTextVersion(entry);
  next.textVersion = expected + (sameScriptText(entry, next) ? 0 : 1);
  let savedEntry = next;
  drafts.set(id, next);
  const written = persist("draft", id, next, {
    confirmed: true, expectedTextVersion: expected, textOnly: true,
    onDraftSaved: (saved) => {
      savedEntry = saved as ScriptEntry;
      if (drafts.get(id) === next) {
        drafts.set(id, hydrateDraft(savedEntry));
        version++;
        listeners.forEach((listener) => listener());
      }
    },
  });
  version++;
  listeners.forEach((l) => l());
  return written.then((saved) => options ? { saved, entry: savedEntry } : undefined);
}

/**
 * Denormalize a script's WEEK and day index from a saved week plan. The plan
 * is the truth; `week` and `videoNum` are the caches every other surface still
 * reads — the Videos-page grouping, the editor's `#video-N` anchors,
 * openScriptsFor's sort — so edit mode rewrites them on Save. `week` moves only on
 * a cross-week drag; within a week it is a no-op. No-op unless something actually
 * changed, and no-op on a non-draft (every script is a draft now). Returns the
 * durable write so a caller saving a whole plan can await the batch.
 */
export function setScriptSlot(id: string, week: number, videoNum: number): Promise<void>;
export function setScriptSlot(id: string, week: number, videoNum: number, options: { confirmed: true }): Promise<boolean>;
export function setScriptSlot(id: string, week: number, videoNum: number, options?: { confirmed: true }): Promise<void | boolean> {
  const entry = drafts.get(id);
  if (!entry) return Promise.resolve(options?.confirmed ? false : undefined);
  // Confirmation also retries an optimistic slot whose earlier write failed.
  if (!options?.confirmed && entry.week === week && entry.videoNum === videoNum) return Promise.resolve();
  const next = { ...entry, week, videoNum };
  drafts.set(id, next);
  const written = options?.confirmed
    ? persist("draft", id, next, options)
    : persist("draft", id, next);
  version++;
  listeners.forEach((l) => l());
  return written;
}

/**
 * Move a script to another WEEK, and touch nothing else — the script half of an
 * AM renaming a week (lib/week-renumber.ts).
 *
 * setScriptSlot cannot do this job, and the reason is not tidiness. It writes
 * `week` and `videoNum` together, so a caller renaming a week has to hand back a
 * day index — and the only one it has is the one it read before the write. The
 * heading's popup can change the date and the number in a single Save, the date
 * half re-sorts the week's days, and the rename half would then stamp the
 * pre-sort order back on top of it. A writer that has no opinion about the day
 * index cannot have that bug.
 */
export function setScriptWeek(id: string, week: number): Promise<void> {
  const entry = drafts.get(id);
  if (!entry || entry.week === week) return Promise.resolve();
  const next = { ...entry, week };
  drafts.set(id, next);
  const written = persist("draft", id, next);
  version++;
  listeners.forEach((l) => l());
  return written;
}

/**
 * Re-key a script onto another brand — the script half of an admin moving a week
 * (the retired brand move, lib/week-reassign.ts), and the only thing in the app that writes `brandSlug`
 * after a draft is born.
 *
 * It writes the KEY and its label together, because a row carrying one brand's
 * slug and another's name is worse than either: the page filters on the slug and
 * every list renders the name, so the script would sit in one brand's table
 * wearing the other's name.
 *
 * ⚠️ `brandNameProvided` IS NOT TOUCHED. It looks like a third copy of the same
 * fact and is not one — it is the brand name the CREATOR typed into their doc,
 * a graded field the rubric checks the hook against (ironside-script-data). An
 * admin correcting where a week is filed is not editing what the creator wrote.
 */
export function setScriptBrand(id: string, brandSlug: string, brandName: string): Promise<void> {
  const entry = drafts.get(id);
  if (!entry || (entry.brandSlug === brandSlug && entry.brandName === brandName)) return Promise.resolve();
  const next = { ...entry, brandSlug, brandName };
  drafts.set(id, next);
  const written = persist("draft", id, next);
  version++;
  listeners.forEach((l) => l());
  return written;
}

/**
 * Remove a script and everything hanging off its id — the script half of an
 * admin deleting a week (lib/week-delete.ts). The ONLY thing in the app that
 * takes a script away.
 *
 * A TOMBSTONE, NOT A DELETE, for the two reasons clearApproved gives: /api/store's
 * DELETE is an allowlist that deliberately refuses `draft` ("a `draft` IS a
 * script … nothing in the app has any business removing one over a query
 * string", tests/unit/store-route-delete.test.ts). (Its old second reason, that
 * a row removed server-side kept rendering in every other open tab, went with
 * the store diff.)
 *
 * AND THE TOMBSTONE IS WHY THE ID IS SAFE. A draft id is `DRAFT-<CREATOR>-<n>`
 * off a counter that hydration re-derives from the ids in the dump. Delete the
 * rows outright and the counter comes back LOWER after a reload, mints the same
 * id again, and the new script inherits whatever the old one left behind — its
 * stage, its grade, its comments. The null row keeps the id spoken for forever
 * (bumpDraftCounterFor runs on the tombstone too), so that cannot happen.
 *
 * EVERY KEY THE ID OWNS GOES WITH IT, and each is retracted only if it existed —
 * an untouched draft writes two rows (itself and its grade, which is retracted
 * always: below), not seven. The one exception is COMMENTS,
 * which are kept: they already carry their own tombstone for a different reason
 * ("retention is audit"), the thread is unreachable the moment the script is
 * gone, and no future script can reach it because no future script gets this id.
 *
 * ⚠️ THE FOOTAGE AND THE UNDO POINTS ARE THE SERVER'S TO RETRACT. They are in
 * the `editor` store group, which a page need not load, so this tab may hold
 * none of them. POST /api/store retracts
 * both, with their audit rows, in the draft tombstone's own transaction
 * (retractEditorRows). Here they only leave this tab's maps, at once; the diff's
 * null rows find nothing more to drop.
 */
export function deleteScript(id: string): Promise<void> {
  if (!drafts.delete(id)) return Promise.resolve(); // no such script — write nothing

  const writes = [persist("draft", id, null)];
  if (overrides.delete(id)) writes.push(persist("stage_override", id, null));
  if (footageStages.delete(id)) writes.push(persist("video_stage", id, null));
  driveVideos.delete(id);
  // THE GRADE WHETHER OR NOT THIS TAB HOLDS IT. A tab no longer holds every
  // grade — only the ones its editor asked for or a diff brought — so "only if
  // it existed" would leave a deleted script's grade on the server for good,
  // under an id nothing can reach. The cost is one null row for a script that
  // was never graded.
  gradeRecords.delete(id);
  writes.push(persist("grade", id, null));
  touchGrade(id);
  gradedThisSession.delete(id);
  for (const ladder of ["script", "footage"] as Ladder[]) {
    const key = `${ladder}:${id}`;
    if (approvedOnByKey.delete(key)) writes.push(persist("approved_on", key, null));
    undoPoints.delete(key); // the server retracts it (above)
  }

  version++;
  listeners.forEach((l) => l());
  return Promise.all(writes).then(() => undefined);
}

/**
 * Like createDraftScript but targets a specific week/videoNum slot — used when a
 * creator starts typing in an empty slot. Idempotent: returns the existing slot
 * entry if one is already there, so it's safe to call on the first keystroke.
 */
export function createDraftForSlot(
  creatorEmail: string,
  week: number,
  videoNum: number,
  owner?: DraftOwner,
): ScriptEntry {
  // Scope the idempotency check to the brand the caller names — a multi-brand
  // creator has a video `videoNum` in each brand's copy of this week, and a
  // brand-blind check would return the wrong brand's draft. Callers that pass no
  // brand (a single-brand creator) get today's behaviour.
  const existing = weekSlotsFor(creatorEmail, week, owner?.brandSlug).find((s) => s.videoNum === videoNum)?.entry;
  if (existing) return existing; // idempotent

  const entry = newDraftEntry(creatorEmail, week, videoNum, owner);
  drafts.set(entry.id, entry);
  persist("draft", entry.id, entry);
  version++;
  listeners.forEach((l) => l());
  return entry;
}

/**
 * Mint a draft that is BORN WITH ITS TEXT IN IT — the one write an import makes
 * per script.
 *
 * ⚠️ ONE WRITE PER DRAFT, AND THAT IS THE WHOLE POINT OF THIS FUNCTION. persist()
 * fires one POST per call and nothing orders two writes to the same row, so
 * `createDraftForSlot` followed by `saveDraftFields` puts a blank row and a full
 * row in the air at once — and the blank one can land last, which is an import
 * that silently imports nothing. The editor never meets this because its autosave
 * waits 800ms; an importer writing five scripts as fast as it can does. So the
 * row is built complete (newDraftEntry, shared with createDraftForSlot so the two
 * cannot drift) and persisted exactly once.
 *
 * ⚠️ IT ALWAYS MINTS, unlike createDraftForSlot, which is idempotent because it
 * is called on every keystroke. Deciding whether this slot may be written is the
 * caller's job and is not a decision this function can second-guess: it is what
 * `isOpenSlot` answers in lib/script-import.ts, and for a slot holding a BLANK
 * draft the caller must `deleteScript` it too, after this mint, rather than
 * leave this row beside it. See the import dialog for why replacing beats
 * writing into one, and why it mints before it deletes.
 *
 * Returns the durable write so the caller can wait for the whole import to land
 * before it closes — never so it can branch on whether the write worked. persist
 * hides that on purpose, and the write-failure notice owns it.
 */
export function mintDraftWithFields(
  creatorEmail: string,
  week: number,
  videoNum: number,
  owner: DraftOwner | undefined,
  fields: Partial<DraftFields>,
): { entry: ScriptEntry; written: Promise<void> } {
  const entry = newDraftEntry(creatorEmail, week, videoNum, owner, fields);
  drafts.set(entry.id, entry);
  const written = persist("draft", entry.id, entry);
  version++;
  listeners.forEach((l) => l());
  return { entry, written };
}

/**
 * A creator's week as slots, filled from existing scripts (seed + in-session
 * drafts), null where nothing's written yet — five of them for a week with no
 * stored plan, and otherwise as many as its plan has days. This is
 * the unit the week-scoped Script Editor renders.
 *
 * PASS `brandSlug` AND IT IS BRAND-SCOPED — the correct call. It narrows to
 * that brand and reads its saved `week_plan`, so the order matches the Videos
 * page and a SKIPPED day comes back skipped. A creator who works two brands has
 * one week 3 in EACH; without the brand the two collide on video number and
 * weekSlotsFrom drops one of every pair. The brand-less overload survives only
 * for the legacy single-brand callers inside this store (weekPhase and
 * friends), where a creator has exactly one brand and there is nothing to
 * collide — every UI caller that has a brand should pass it.
 */
export function weekSlotsFor(
  creatorEmail: string,
  week: number,
  brandSlug?: string,
): WeekSlot[] {
  const scripts = scriptsForCreator(creatorEmail)
    .filter((s) => s.week === week && (brandSlug === undefined || s.brandSlug === brandSlug));
  const plan = brandSlug === undefined ? undefined : getWeekPlan(creatorEmail, brandSlug, week);
  return weekSlotsFrom(scripts, plan);
}

// ─── Week phase rollup — for the week-scoped Script Editor status header ────
// The week's phase is its LEAST-ADVANCED video (slowest-video-wins): three
// approved and two edits-requested reads as Edits requested, because the week
// is not done until its slowest video is.
//
// RANK IS ROW INDEX. The week's phase is the first of nine (PHASE_BY_RANK) that
// matches, top to bottom; this computes a minimum over slots. Those agree only
// if slotRank returns each stage's ROW NUMBER in that table. Note rank 2
// (edits-requested) sits ABOVE rank 1 (awaiting-am-review), so a week holding
// one of each reads "Scripts in review" — that is what the ordering says, and
// it is the easiest thing here to get backwards.
//
// The rank is therefore a DISPLAY order, not a progress order: the perfectly
// legal resubmit edits-requested --submit--> awaiting-am-review moves rank
// DOWN, and a week's phase moves backward when the brand requests edits. Both
// are correct and visible.
//
// Stage-based, not grade-based. A sub-80 script that an AM approved anyway does
// not drag the week back to writing. The grade's score gates nothing at all —
// it only raises a warning.

export type WeekPhase =
  | "scripts-to-be-submitted"
  | "scripts-awaiting-am-review"
  | "scripts-edits-requested"
  | "scripts-awaiting-brand-review"
  | "footage-to-be-uploaded"
  | "footage-awaiting-am-review"
  | "footage-edits-requested"
  | "footage-awaiting-brand-review"
  | "complete";

const PHASE_BY_RANK: WeekPhase[] = [
  "scripts-to-be-submitted",
  "scripts-awaiting-am-review",
  "scripts-edits-requested",
  "scripts-awaiting-brand-review",
  "footage-to-be-uploaded",
  "footage-awaiting-am-review",
  "footage-edits-requested",
  "footage-awaiting-brand-review",
  "complete",
];

const SCRIPT_RANK: Record<ScriptStage, number> = {
  "to-be-submitted": 0,
  "awaiting-am-review": 1,
  "edits-requested": 2,
  "awaiting-brand-review": 3,
  approved: 4, // …then the footage ladder takes over
};

const FOOTAGE_RANK: Record<FootageStage, number> = {
  "to-be-uploaded": 4,
  "awaiting-am-review": 5,
  "edits-requested": 6,
  "awaiting-brand-review": 7,
  approved: 8,
};

/** Rank 0..8 for one video slot. An empty slot ranks 0 — nothing to submit yet. */
function slotRank(entry: ScriptEntry | null): number {
  if (!entry) return 0;
  const stage = effectiveStage(entry.id, entry.stage);
  if (!footageUnlocked(stage)) return SCRIPT_RANK[stage];
  return FOOTAGE_RANK[footageStage(entry.id)];
}

/** One slot's phase — the per-video answer weekPhase takes the minimum of. */
export function slotPhase(entry: ScriptEntry | null): WeekPhase {
  return PHASE_BY_RANK[slotRank(entry)];
}

/** Display order of a phase: its row in PHASE_BY_RANK. For min/max over slots. */
export function phaseRank(phase: WeekPhase): number {
  return PHASE_BY_RANK.indexOf(phase);
}

/**
 * The week's phase from its slots — the pure core, so the skip rule is testable
 * without a store. Skipped days do not vote: no video goes out that day, so
 * it has no phase to drag the week's minimum down to `to-be-submitted`. A week
 * that is nothing BUT skips has no phase at all — no video anywhere in it — and
 * reads as rank 0, the same vacuous answer an all-empty week already gives.
 */
export function weekPhaseOf(slots: WeekSlot[]): WeekPhase {
  const ranks = slots.filter((s) => !s.skipped).map((s) => slotRank(s.entry));
  return PHASE_BY_RANK[ranks.length ? Math.min(...ranks) : 0];
}

export function weekPhase(creatorEmail: string, week: number): WeekPhase {
  return weekPhaseOf(weekSlotsFor(creatorEmail, week));
}

/**
 * How many of the week's videos sit at each phase — the status bar's stat
 * lines. weekPhase collapses the same numbers to their minimum; this keeps
 * them apart, so "Edits requested" can also say "and 3 of 5 are approved".
 */
export function weekPhaseCounts(slots: WeekSlot[]): Partial<Record<WeekPhase, number>> {
  const counts: Partial<Record<WeekPhase, number>> = {};
  for (const { entry, skipped } of slots) {
    if (skipped) continue; // a skipped day is not a video and does not vote
    const phase = slotPhase(entry);
    counts[phase] = (counts[phase] ?? 0) + 1;
  }
  return counts;
}

/**
 * The pre-submission breakdown. Every unsubmitted slot ranks as the same
 * `scripts-to-be-submitted` phase whether it is empty, written-but-unchecked,
 * or ready — this is the function that tells those apart, for the writing-stage
 * lines on the creator's and writer's bar ("2 of 5 still need to be written").
 * An empty slot and a blank draft are the same thing to a reader, so they count
 * together (isBlankFields, the grading rule).
 *
 * ⚠️ ONLY SCRIPTS STILL AT `to-be-submitted` ARE COUNTED, and that is new.
 * While the week went in as a batch, every script was pre-submission at once
 * and the question never arose. Scripts now go in one at a time, so a week
 * holds submitted and unsubmitted scripts side by side — and a sub-80 grade on
 * a script the AM is already reading is not a nudge to anybody. `total` is
 * still every active day, so the lines read "1 of 5".
 */
export interface WeekCompleteness {
  /** Empty slot, or a draft with nothing in it. */
  notWritten: number;
  /**
   * Written, never over the gate — "checks not run". EXACTLY the scripts whose
   * Submit is greyed out for want of a grade: passedGradeGate, the store's own gate.
   */
  unchecked: number;
  /** Written and graded. Includes the belowThreshold ones — graded is graded. */
  ready: number;
  /**
   * Graded under SUBMIT_THRESHOLD (80) — a nudge line, never a gate. A low
   * grade goes in with a count next to it.
   */
  belowThreshold: number;
  total: number;
}

export function weekCompleteness(slots: WeekSlot[]): WeekCompleteness {
  // Only ACTIVE days count. A skipped day is not a missing video — no video is
  // ever going out that day — so it leaves the denominator as well as every
  // numerator. `total` falls out as the active count, and "2 of 5 still need to
  // be written" becomes "1 of 4" with no copy change, since the bar interpolates
  // `total`.
  const active = slots.filter((s) => !s.skipped);
  const c: WeekCompleteness = { notWritten: 0, unchecked: 0, ready: 0, belowThreshold: 0, total: active.length };
  for (const { entry } of active) {
    if (!entry) {
      c.notWritten++;
      continue;
    }
    // Submitted (or further): past the writing stage, whatever its grade says.
    if (effectiveStage(entry.id, entry.stage) !== "to-be-submitted") continue;
    if (isBlankFields(fieldsFromEntry(entry))) c.notWritten++;
    else if (!passedGradeGate(entry)) c.unchecked++;
    else {
      c.ready++;
      if (entry.grade !== null && entry.grade < SUBMIT_THRESHOLD) c.belowThreshold++;
    }
  }
  return c;
}

/**
 * Everything the status bar's rollup (lib/editor-rollup.ts) needs from a week's
 * slots, in one place so the bar and the tests that stand in for it compose it
 * the same way.
 *
 * A SKIPPED DAY IS NOT A VIDEO, so it gets no phase to vote with — the rule
 * weekPhaseOf, weekCompleteness and pendingOn already keep. The bar used to map
 * every slot, so a skipped day reached it as an empty `to-be-submitted` one and
 * pinned the headline to "Scripts to be submitted" on a week whose every real
 * video had gone in, over a "more to submit" count of zero.
 */
export function rollupInputs(slots: WeekSlot[]): {
  slotPhases: WeekPhase[];
  completeness: WeekCompleteness;
  ladderSlots: LadderSlot[];
} {
  return {
    slotPhases: slots.filter((s) => !s.skipped).map((s) => slotPhase(s.entry)),
    completeness: weekCompleteness(slots),
    ladderSlots: ladderSlotsOf(slots),
  };
}

/**
 * The week's slots as the "what is left?" rule reads them — pendingOn in
 * lib/week-slots.ts, which the server's submit notice also asks. The client's
 * half of that bridge: the stages come out of this store (overrides, footage),
 * the rule does not.
 *
 * An unwritten day is `script: null`, and its video is owed like any other.
 * Skipped days pass through with their flag; pendingOn drops them itself.
 */
export function ladderSlotsOf(slots: WeekSlot[]): LadderSlot[] {
  return slots.map(({ entry, skipped }) => ({
    skipped,
    script: entry ? effectiveStage(entry.id, entry.stage) : null,
    footage: entry ? footageStage(entry.id) : "to-be-uploaded",
  }));
}

/**
 * The creator's own scripts still waiting on them — never submitted, or sent
 * back for edits. Sorted week-descending, so the editor's resolveWeek lands a
 * creator on their most recent week with work in it.
 */
export function openScriptsFor(creatorEmail: string): ScriptEntry[] {
  return scriptsForCreator(creatorEmail)
    .filter((s) => {
      const stage = effectiveStage(s.id, s.stage);
      return stage === "to-be-submitted" || stage === "edits-requested";
    })
    .sort((a, b) => b.week - a.week || a.videoNum - b.videoNum);
}

// ─── Hydration from the server ───────────────────────────────────────────────
// Merge server rows in place — never clear(): a key leaves only when the server
// says it was deleted (the `deleted` list), so an empty answer can't blank out
// local state. Skip any key with an unconfirmed local write (isConfirmed) so a
// poll can't revert the acting user's own change. Only bump version when
// something actually moved — a row equal to what the map already holds
// (lib/same-data.ts) is not a move, so a poll that brings nothing new wakes no
// subscriber. Does NOT call persist (that would echo writes straight back to
// the server).

/**
 * A persisted draft, brought up to the CURRENT ScriptEntry shape.
 *
 * `r.data as ScriptEntry` is a claim about a row that may have been written
 * months ago, and it is the only place an old shape enters the running app. A
 * field the schema grew after a row was written defaults empty here, and some
 * stored drafts carry neither `cta` nor `valueProp`.
 *
 * Keep truly new-field defaults here whenever ScriptEntry grows.
 */
function hydrateDraft(data: unknown): ScriptEntry {
  const e = data as ScriptEntry;
  return {
    ...e,
    scriptAngle: e.scriptAngle ?? "",
    cta: e.cta ?? "",
    valueProp: e.valueProp ?? "",
    // A row graded before `firstGradedAt` existed has been graded — it holds a
    // grade — and when is not recorded anywhere this loader can see. It gets the
    // sentinel, so the next write of the row carries the gate with it and a
    // later admin reset (which clears `grade`) cannot close it again.
    firstGradedAt: e.firstGradedAt ?? (e.grade !== null && e.grade !== undefined ? GRADED_BEFORE_RECORDED : null),
  };
}

/** Bump draftCounter past a hydrated draft id's numeric suffix so a reset
 *  counter can't re-mint a colliding `DRAFT-…-<n>` id after reload. */
function bumpDraftCounterFor(id: string): void {
  const m = /-(\d+)$/.exec(id);
  if (m) draftCounter = Math.max(draftCounter, Number(m[1]));
}

/**
 * Which map a null row empties, by kind — the read half of every tombstone this
 * store writes. Declared here rather than beside the maps because it names all
 * of them, and a Map cannot be referenced before it exists.
 *
 * The keys ARE the list of kinds that may be retracted. A kind missing from it
 * falls through to the switch below and is upserted, which for a null row would
 * mean storing a null where a value is expected — so adding a persist(kind, id,
 * null) anywhere means adding its kind here, in the same commit.
 */
const RETRACTABLE: Record<string, Map<string, unknown>> = {
  draft: drafts as Map<string, unknown>,
  stage_override: overrides as Map<string, unknown>,
  video_stage: footageStages as Map<string, unknown>,
  drive_video: driveVideos as Map<string, unknown>,
  grade: gradeRecords as Map<string, unknown>,
  approved_on: approvedOnByKey as Map<string, unknown>,
  stage_undo: undoPoints as Map<string, unknown>,
};

/** Set `id` to the server's value unless the map already holds the same thing. True when it moved. */
function upsert<T>(map: Map<string, T>, id: string, value: T): boolean {
  if (map.has(id) && sameData(map.get(id), value)) return false;
  map.set(id, value);
  return true;
}

/**
 * Every map this store keys by a server row, by kind: the RETRACTABLE kinds plus
 * `comment`. A key DELETED on the server (the diff's `deleted`) leaves whichever
 * map holds it — a comment too, though a null row for one is not a removal
 * (below): a hard delete is the row being gone, whatever it said.
 */
const HELD_BY_KIND: Record<string, Map<string, unknown>> = {
  ...RETRACTABLE,
  comment: comments as Map<string, unknown>,
};

export function applyServerRows(
  rows: { kind: string; id: string; data: unknown }[],
  deleted: { kind: string; id: string }[] = [],
): void {
  let changed = false;
  for (const d of deleted) {
    const map = HELD_BY_KIND[d.kind];
    if (!map) continue; // another store's kind
    // A deletion is the server answering "null" for the key: our own unechoed
    // write to it wins, exactly as it does over a row (store-persist.ts).
    // A diff spoke for this grade (the grade-knowledge header), pending write or not.
    if (d.kind === "grade") touchGrade(d.id);
    if (!isConfirmed(d.kind, d.id, null)) continue;
    // As for a tombstone, below: the id stays spoken for, and nobody asked for it.
    if (d.kind === "draft") bumpDraftCounterFor(d.id);
    if (d.kind === "grade") gradedThisSession.delete(d.id);
    if (map.delete(d.id)) changed = true;
  }
  for (const r of rows) {
    if (r.kind === "grade") touchGrade(r.id); // as for a deletion, above
    if (!isConfirmed(r.kind, r.id, r.data)) continue; // our own in-flight write — leave it
    // A NULL `data` IS A TOMBSTONE, on every kind this store keys by a script:
    // a removed video (applyFootageAction "remove"), the date of a retracted
    // approval (clearApproved), and — since a week can be deleted — the script
    // row itself and everything hanging off its id (deleteScript). All are
    // written as a null row rather than a DELETE, so this is where the poll
    // turns one back into an absence, and `.set(id, null as T)` would be worse
    // than useless: getDriveVideo would answer a null that every caller expects
    // to be a DriveVideo, and findScript a null that means "no such script".
    //
    // `comment` is deliberately absent: a deleted comment carries its tombstone
    // INSIDE the row (`deleted: true`) so the text survives for audit, and a
    // null row for one would be a second, disagreeing way of saying the same
    // thing.
    if (r.data === null) {
      const retracted = RETRACTABLE[r.kind];
      if (retracted) {
        // BEFORE the delete, and whether or not the row was here: the whole
        // point of a draft tombstone is that the id it names stays spoken for,
        // so a counter re-derived from this dump can never mint it again.
        if (r.kind === "draft") bumpDraftCounterFor(r.id);
        if (r.kind === "grade") gradedThisSession.delete(r.id);
        if (retracted.delete(r.id)) changed = true;
        continue;
      }
    }
    switch (r.kind) {
      case "stage_override": if (upsert(overrides, r.id, r.data as ScriptStage)) changed = true; break;
      case "drive_video":    if (upsert(driveVideos, r.id, r.data as DriveVideo)) changed = true; break;
      case "video_stage":    if (upsert(footageStages, r.id, r.data as FootageStage)) changed = true; break;
      case "comment":        if (upsert(comments, r.id, r.data as Comment)) changed = true; break; // per-comment
      // The counter is bumped whether or not the draft moved: it is cheap, and
      // it is what keeps a re-minted id impossible, so it never waits on a diff.
      case "draft":          bumpDraftCounterFor(r.id); if (upsert(drafts, r.id, hydrateDraft(r.data))) changed = true; break;
      case "grade": {
        const incoming = r.data as ClientGradeRecord;
        const previous = gradeRecords.get(r.id);
        // Shared diffs omit document-derived feedback. Keep our scoped copy
        // only for the same run; a new run asks the scoped endpoint again.
        const next = !incoming.brandRequirements && previous && previous.runId === incoming.runId
          ? { ...incoming,
            ...(previous.brandRequirements ? { brandRequirements: previous.brandRequirements } : {}),
            ...(previous.brandDocumentsChanged ? { brandDocumentsChanged: true } : {}),
          } : incoming;
        if (upsert(gradeRecords, r.id, next)) changed = true;
        if (incoming.brandRequirementsAvailable && !incoming.brandRequirements && gradeScoped.has(r.id) && previous?.runId !== incoming.runId) {
          // Keep the standard grade usable while refreshing the scoped detail.
          queueMicrotask(() => { void askForGrades([r.id]); });
        }
        break;
      }
      // id is already `${ladder}:${scriptId}` — the key markApproved wrote.
      case "approved_on":    if (upsert(approvedOnByKey, r.id, r.data as string)) changed = true; break;
      // Same `${ladder}:${scriptId}` key, written by recordUndoPoint. A move
      // that arrives from another tab is as good as one made here: the undo it
      // offers is checked against the stage as it stands (undoneScriptStage),
      // not against who is holding it.
      case "stage_undo":     if (upsert(undoPoints, r.id, r.data as ScriptMove | FootageMove)) changed = true; break;
      // other kinds belong to another store — ignore
    }
  }
  if (changed) { version++; listeners.forEach((l) => l()); }
}
